import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import IconButton from "@material-ui/core/IconButton";
import Checkbox from "@material-ui/core/Checkbox";

import SupervisorAccountIcon from "@material-ui/icons/SupervisorAccount";
import StoreIcon from "@material-ui/icons/Store";
import VerifiedUserIcon from "@material-ui/icons/VerifiedUser";
import BlockIcon from "@material-ui/icons/Block";
import RefreshIcon from "@material-ui/icons/Refresh";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";

import ErrorPage from "commonComponents/errorPage/ErrorPage";
import DialogShell from "designV2/components/DialogShell";
import PageHeader from "designV2/components/PageHeader";
import MetricCard from "designV2/components/MetricCard";
import SearchField from "designV2/components/SearchField";
import FilterDropdown from "designV2/components/FilterDropdown";
import ActiveFilterChipList from "designV2/components/ActiveFilterChipList";
import StatusBadge from "designV2/components/StatusBadge";
import TableToolbar from "designV2/components/TableToolbar";
import Button from "designV2/components/Button";
import EmptyState from "designV2/components/EmptyState";
import PaginationComponent from "designV2/components/Pagination";
import { MetricsSkeleton, TableSkeleton } from "designV2/components/Skeleton";
import { endPoint } from "utils/config";
import { Avatar, readableError } from "superAdminViews/users/peopleFields";
import "superAdminViews/users/superPeople.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 * ------------------------------------------------------------------ */

const ADMIN_FILTERS = `
  _id: $_id
  fullName: $fullName
  email: $email
  phoneNumber: $phoneNumber
  type: $type
  state: $state
  shopName: $shopName
`;

const ADMIN_VARIABLES = `
  $limit: Int
  $skip: Int
  $_id: ID
  $fullName: String
  $email: String
  $phoneNumber: String
  $type: String
  $state: AdminState
  $shopName: String
  $createdAtSort: Int
`;

const GET_ADMINS = gql`
  query getAdminsByAdmin(${ADMIN_VARIABLES}) {
    getAdminsByAdmin(
      pagination: { limit: $limit, skip: $skip }
      filters: { ${ADMIN_FILTERS} }
      sort: { createdAt: $createdAtSort }
    ) {
      _id
      fullName
      phoneNumber
      email
      type
      state
      createdAt
      shop {
        _id
        name
      }
      # Role is keyed on id, not _id: the only type in the schema that is.
      roles {
        id
        name
      }
    }
    getAdminsByAdminCount(filters: { ${ADMIN_FILTERS} })
  }
`;

const GET_ADMIN_COUNTS = gql`
  query getAdminCounts {
    total: getAdminsByAdminCount(filters: {})
    active: getAdminsByAdminCount(filters: { state: ACTIVE })
    suspended: getAdminsByAdminCount(filters: { state: SUSPENDED })
    shopAdmins: getAdminsByAdminCount(filters: { type: "SHOP-ADMIN" })
  }
`;

const SUSPEND_ADMINS = gql`
  mutation suspendAdminByAdmin($idSet: [ID!]!) {
    suspendAdminByAdmin(idSet: $idSet) {
      _id
      state
    }
  }
`;

const DELETE_ADMINS = gql`
  mutation deleteAdminBySystemAdmin($idSet: [ID!]!) {
    deleteAdminBySystemAdmin(idSet: $idSet) {
      _id
    }
  }
`;

/* ------------------------------------------------------------------ *
 * Constants
 * ------------------------------------------------------------------ */

const TYPE_OPTIONS = [
  { value: "SUPER-ADMIN", label: "Super admin" },
  { value: "SHOP-ADMIN", label: "Shop admin" },
];
const TYPE_LABELS = TYPE_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const STATE_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "SUSPENDED", label: "Suspended" },
];
const STATE_LABELS = STATE_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const SEARCH_TARGETS = [
  { value: "fullName", label: "Name" },
  { value: "email", label: "Email" },
  { value: "phoneNumber", label: "Phone" },
  { value: "shopName", label: "Shop" },
];
const SEARCH_LABELS = SEARCH_TARGETS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
];

const FILTER_DEBOUNCE_MS = 350;

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

const Admins = () => {
  const [searchTarget, setSearchTarget] = useState("fullName");
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [sortOption, setSortOption] = useState("newest");

  const [pageCount, setPageCount] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const [selectedIds, setSelectedIds] = useState([]);
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [working, setWorking] = useState(false);
  const [actionError, setActionError] = useState("");

  const [variables, setVariables] = useState({ limit: perPage, skip: 0, createdAtSort: -1 });

  useEffect(() => {
    const handler = setTimeout(() => {
      const term = searchTerm.trim();
      setVariables((v) => ({
        ...v,
        skip: 0,
        fullName: undefined,
        email: undefined,
        phoneNumber: undefined,
        shopName: undefined,
        ...(term ? { [searchTarget]: term } : {}),
        type: typeFilter || undefined,
        state: stateFilter || undefined,
        createdAtSort: sortOption === "oldest" ? 1 : -1,
      }));
      setPageCount(0);
      setSelectedIds([]);
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [searchTarget, searchTerm, typeFilter, stateFilter, sortOption]);

  useEffect(() => {
    setVariables((v) => ({ ...v, limit: perPage, skip: perPage * pageCount }));
    setSelectedIds([]);
  }, [perPage, pageCount]);

  const { data, loading, error, refetch } = useQuery(GET_ADMINS, {
    variables,
    fetchPolicy: "network-only",
  });
  const { data: countsData, refetch: refetchCounts } = useQuery(GET_ADMIN_COUNTS, {
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });
  const [suspendAdmins] = useMutation(SUSPEND_ADMINS);
  const [deleteAdmins] = useMutation(DELETE_ADMINS);

  const admins = (data && data.getAdminsByAdmin) || [];
  const totalCount = (data && data.getAdminsByAdminCount) || 0;
  const counts = countsData || {};

  /* ---------------- selection + actions ---------------- */

  const toggleSelectOne = (id) =>
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : current.concat(id)
    );

  const toggleSelectAll = () => {
    const ids = admins.map((a) => a._id);
    setSelectedIds((current) => (current.length === ids.length ? [] : ids));
  };

  const checkboxState =
    admins.length > 0 && selectedIds.length === admins.length
      ? "checked"
      : selectedIds.length > 0
      ? "indeterminate"
      : "unchecked";

  const runAction = (mutate, targets, done) => {
    setWorking(true);
    setActionError("");
    mutate({ variables: { idSet: targets.map((a) => a._id) } })
      .then(() => {
        setWorking(false);
        done();
        setSelectedIds([]);
        refetch();
        refetchCounts();
      })
      .catch((err) => {
        setWorking(false);
        done();
        setActionError(readableError(err));
      });
  };

  /* ---------------- filter chips ---------------- */

  const trimmedTerm = searchTerm.trim();
  const activeFilterChips = [];
  if (trimmedTerm) {
    activeFilterChips.push({ id: "search", label: `${SEARCH_LABELS[searchTarget]}: ${trimmedTerm}` });
  }
  if (typeFilter) activeFilterChips.push({ id: "type", label: `Type: ${TYPE_LABELS[typeFilter]}` });
  if (stateFilter) activeFilterChips.push({ id: "state", label: `State: ${STATE_LABELS[stateFilter]}` });

  const clearFilter = (id) => {
    if (id === "search") setSearchTerm("");
    if (id === "type") setTypeFilter("");
    if (id === "state") setStateFilter("");
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setTypeFilter("");
    setStateFilter("");
  };

  /* ---------------- render ---------------- */

  if (loading && !data) {
    return (
      <div className="page superPeoplePage superAdminsPage">
        <main className="main">
          <PageHeader title="Admins" description="Everyone with access to an admin panel." />
          <MetricsSkeleton count={4} />
          <TableSkeleton rows={6} columns={6} />
        </main>
      </div>
    );
  }

  if (error) {
    return <ErrorPage />;
  }

  const selectedAdmins = admins.filter((a) => selectedIds.includes(a._id));

  return (
    <div className="page superPeoplePage superAdminsPage">
      <main className="main">
        <PageHeader
          title="Admins"
          count={totalCount}
          description="Everyone with access to an admin panel, what kind of access they have, and which shop they belong to."
          actions={
            <Button
              variant="primary"
              onClick={() => (window.location.href = `${endPoint}/superAdmin/addAdmin`)}
            >
              New admin
            </Button>
          }
        />

        <section className="stats">
          <MetricCard icon={<SupervisorAccountIcon />} title="Matching admins" value={totalCount} />
          <MetricCard
            icon={<VerifiedUserIcon />}
            title="Active"
            value={counts.active || 0}
            tone="success"
          />
          <MetricCard icon={<StoreIcon />} title="Shop admins" value={counts.shopAdmins || 0} />
          <MetricCard
            icon={<BlockIcon />}
            title="Suspended"
            value={counts.suspended || 0}
            tone={counts.suspended > 0 ? "danger" : "default"}
          />
        </section>

        <section className="filters">
          <SearchField
            placeholder={`Search by ${SEARCH_LABELS[searchTarget].toLowerCase()}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="filtersSelects">
            <FilterDropdown
              label={`Search in: ${SEARCH_LABELS[searchTarget]}`}
              options={SEARCH_TARGETS}
              value={searchTarget}
              active={false}
              onChange={(e) => setSearchTarget(e.target.value)}
            />
            <FilterDropdown
              label={typeFilter ? `Type: ${TYPE_LABELS[typeFilter]}` : "Type"}
              options={[{ value: "", label: "All types" }, ...TYPE_OPTIONS]}
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            />
            <FilterDropdown
              label={stateFilter ? `State: ${STATE_LABELS[stateFilter]}` : "State"}
              options={[{ value: "", label: "Any state" }, ...STATE_OPTIONS]}
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
            />
            <FilterDropdown
              label={`Sort: ${SORT_OPTIONS.find((o) => o.value === sortOption).label}`}
              options={SORT_OPTIONS}
              value={sortOption}
              active={false}
              align="right"
              onChange={(e) => setSortOption(e.target.value)}
            />
          </div>
        </section>

        {activeFilterChips.length > 0 && (
          <ActiveFilterChipList
            filters={activeFilterChips}
            onClearFilter={clearFilter}
            onClearAll={clearAllFilters}
          />
        )}

        <TableToolbar
          selectedCount={selectedIds.length}
          checkboxState={checkboxState}
          onCheckboxChange={toggleSelectAll}
          bulkActions={
            <React.Fragment>
              <Button
                variant="secondary"
                size="sm"
                icon={<PauseCircleOutlineIcon fontSize="small" />}
                onClick={() => setSuspendTarget(selectedAdmins)}
              >
                Suspend
              </Button>
              <Button
                variant="dangerQuiet"
                size="sm"
                icon={<DeleteIcon fontSize="small" />}
                onClick={() => setDeleteTarget(selectedAdmins)}
              >
                Delete
              </Button>
            </React.Fragment>
          }
          rightActions={
            <IconButton
              className="tableTopIconButton"
              size="small"
              onClick={() => {
                refetch();
                refetchCounts();
              }}
              title="Refresh admins"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          }
        />

        {actionError && (
          <div className="dsFormMessage error" role="alert">
            {actionError}
          </div>
        )}

        <div className={`tableResponsive ${loading && data ? "isRefetching" : ""}`}>
          {loading && data && <span className="superPeopleSpinner" aria-hidden="true" />}
          <section className="table">
            <div className="tableHeader">
              <div className="tableHeaderCell tableHeaderCheckbox">
                <Checkbox
                  size="small"
                  checked={checkboxState === "checked"}
                  indeterminate={checkboxState === "indeterminate"}
                  onChange={toggleSelectAll}
                  inputProps={{ "aria-label": "Select every admin on this page" }}
                />
              </div>
              <div className="tableHeaderCell">Admin</div>
              <div className="tableHeaderCell">Contact</div>
              <div className="tableHeaderCell">Access</div>
              <div className="tableHeaderCell">State</div>
              <div className="tableHeaderCell tableHeaderAction">Actions</div>
            </div>

            {admins.length === 0 ? (
              <EmptyState
                icon={<SupervisorAccountIcon />}
                title={activeFilterChips.length === 0 ? "No admins yet" : "No matches"}
                message={
                  activeFilterChips.length === 0
                    ? "Create an admin to give someone access to a panel."
                    : "No admins match these filters. Try widening or clearing them."
                }
              />
            ) : (
              admins.map((admin) => {
                const roles = (admin.roles || []).map((r) => r.name).filter(Boolean);
                return (
                  <div className="row" key={admin._id}>
                    <Checkbox
                      size="small"
                      checked={selectedIds.includes(admin._id)}
                      onChange={() => toggleSelectOne(admin._id)}
                      inputProps={{ "aria-label": `Select ${admin.fullName}` }}
                    />

                    <div className="superPersonCell" data-label="Admin">
                      <Avatar name={admin.fullName} />
                      <span className="superPersonText">
                        <span className="superPersonName">{admin.fullName || "Unnamed"}</span>
                        <span className="superPersonMeta">
                          {admin.shop ? admin.shop.name : TYPE_LABELS[admin.type] || admin.type}
                        </span>
                      </span>
                    </div>

                    <div className="superPersonText" data-label="Contact">
                      <span className="superPersonName">{admin.email || "No email"}</span>
                      <span className="superPersonMeta">{admin.phoneNumber || "No phone"}</span>
                    </div>

                    <span className="superPersonStack" data-label="Access">
                      <span className="superPersonTag">
                        {TYPE_LABELS[admin.type] || admin.type || "Unknown"}
                      </span>
                      {roles.length > 0 && (
                        <span className="superPersonMeta">
                          {roles.length === 1 ? roles[0] : `${roles[0]} +${roles.length - 1}`}
                        </span>
                      )}
                    </span>

                    <span data-label="State">
                      <StatusBadge
                        status={admin.state === "SUSPENDED" ? "danger" : "success"}
                        label={STATE_LABELS[admin.state] || admin.state || "Unknown"}
                      />
                    </span>

                    <div className="superPersonActions">
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={() =>
                          (window.location.href = `${endPoint}/superAdmin/editAdmin/${admin._id}`)
                        }
                        title="Edit admin"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={() => setSuspendTarget([admin])}
                        title="Suspend admin"
                      >
                        <PauseCircleOutlineIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                );
              })
            )}
          </section>
        </div>

        {totalCount > 0 && (
          <PaginationComponent
            currentPage={pageCount}
            pageSize={perPage}
            totalCount={totalCount}
            pageSizeOptions={[10, 20, 50]}
            onPageChange={(newPage) => setPageCount(newPage)}
            onPageSizeChange={(size) => {
              setPerPage(parseInt(size, 10));
              setPageCount(0);
            }}
          />
        )}
      </main>

      <DialogShell
        open={Boolean(suspendTarget)}
        onClose={() => (working ? null : setSuspendTarget(null))}
        title={suspendTarget && suspendTarget.length > 1 ? "Suspend admins" : "Suspend admin"}
        size="sm"
        actions={[
          { label: "Cancel", onClick: () => setSuspendTarget(null), disabled: working },
          {
            label: "Suspend",
            onClick: () => runAction(suspendAdmins, suspendTarget || [], () => setSuspendTarget(null)),
            variant: "danger",
            loading: working,
          },
        ]}
      >
        <div className="superPersonConfirm">
          {suspendTarget && suspendTarget.length > 1
            ? `${suspendTarget.length} admins will immediately lose access to their panel.`
            : "This admin will immediately lose access to their panel."}
        </div>
      </DialogShell>

      <DialogShell
        open={Boolean(deleteTarget)}
        onClose={() => (working ? null : setDeleteTarget(null))}
        title={deleteTarget && deleteTarget.length > 1 ? "Delete admins" : "Delete admin"}
        size="sm"
        actions={[
          { label: "Cancel", onClick: () => setDeleteTarget(null), disabled: working },
          {
            label: "Delete",
            onClick: () => runAction(deleteAdmins, deleteTarget || [], () => setDeleteTarget(null)),
            variant: "danger",
            loading: working,
          },
        ]}
      >
        <div className="superPersonConfirm">
          {deleteTarget && deleteTarget.length > 1
            ? `${deleteTarget.length} admin accounts will be removed permanently. A shop admin's shop is not deleted with them.`
            : "This admin account will be removed permanently. If they own a shop, the shop is not deleted with them."}
        </div>
      </DialogShell>
    </div>
  );
};

export default Admins;
